import React, { useEffect, useRef, useState } from "react";
import { Share2, Download, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { copyToClipboard, shareCardImage } from "@/lib/share";

export const CARD_W = 1200, CARD_H = 630;
export const GOLD = "#FFB000", PARCH = "#F4F0E6", INK = "#06070A";

export async function loadCardFonts() {
  const fonts = Promise.all([
    document.fonts.load("300 90px Cormorant"),
    document.fonts.load("italic 400 44px Cormorant"),
    document.fonts.load("500 22px 'JetBrains Mono'"),
  ]);
  await Promise.race([fonts, new Promise((r) => setTimeout(r, 2500))]).catch(() => {});
}

export function paintBase(ctx, glowX = 0.72, glowY = 0.45) {
  ctx.fillStyle = INK; ctx.fillRect(0, 0, CARD_W, CARD_H);
  const glow = ctx.createRadialGradient(CARD_W * glowX, CARD_H * glowY, 20, CARD_W * glowX, CARD_H * glowY, 520);
  glow.addColorStop(0, "rgba(255,176,0,0.22)"); glow.addColorStop(1, "rgba(255,176,0,0)");
  ctx.fillStyle = glow; ctx.fillRect(0, 0, CARD_W, CARD_H);
  ctx.strokeStyle = "rgba(255,255,255,0.05)"; ctx.lineWidth = 1;
  for (let x = 0; x < CARD_W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, CARD_H); ctx.stroke(); }
  for (let y = 0; y < CARD_H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(CARD_W, y); ctx.stroke(); }
  ctx.beginPath();
  const r = 24, x = 40, y = 40, w = CARD_W - 80, h = CARD_H - 80;
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  ctx.strokeStyle = "rgba(255,176,0,0.35)"; ctx.lineWidth = 2; ctx.stroke();
  ctx.fillStyle = GOLD; ctx.fillRect(96, 96, 60, 3);
}

export function paintBalls(ctx, whites, powerball, { x = 96, y = 440, r = 52, gap = 24, hits = [], pbHit = false, dimMisses = false } = {}) {
  const hitSet = new Set(hits);
  let cx = x + r;
  const balls = [...whites.map((n) => ({ n, red: false, hit: hitSet.has(n) })), { n: powerball, red: true, hit: pbHit }];
  balls.forEach((b, i) => {
    if (i === 5) cx += 20;
    ctx.save();
    if (dimMisses && !b.hit) ctx.globalAlpha = 0.45;
    ctx.shadowColor = b.hit ? "rgba(255,176,0,0.9)" : b.red ? "rgba(229,9,20,0.7)" : "rgba(255,255,255,0.35)"; ctx.shadowBlur = b.hit ? 40 : 30;
    ctx.beginPath(); ctx.arc(cx, y, r, 0, Math.PI * 2);
    const g = ctx.createRadialGradient(cx - 18, y - 18, 6, cx, y, r);
    if (b.red) { g.addColorStop(0, "#ff5a5f"); g.addColorStop(1, "#b3000a"); } else { g.addColorStop(0, "#ffffff"); g.addColorStop(1, "#d9d4c7"); }
    ctx.fillStyle = g; ctx.fill();
    ctx.restore();
    if (b.hit) { ctx.beginPath(); ctx.arc(cx, y, r + 7, 0, Math.PI * 2); ctx.strokeStyle = GOLD; ctx.lineWidth = 4; ctx.stroke(); }
    ctx.font = `600 ${Math.round(r * 0.77)}px 'JetBrains Mono', monospace`; ctx.fillStyle = b.red ? "#fff" : INK;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(String(b.n).padStart(2, "0"), cx, y + 2);
    ctx.textAlign = "left"; ctx.textBaseline = "top";
    cx += r * 2 + gap;
  });
}

export function paintFooter(ctx, site, line) {
  ctx.font = "500 18px 'JetBrains Mono', monospace"; ctx.letterSpacing = "3px"; ctx.textBaseline = "top";
  ctx.fillStyle = "rgba(244,240,230,0.55)"; ctx.fillText(line, 96, 516);
  ctx.fillStyle = GOLD; ctx.fillText(site.toUpperCase(), 96, 546);
  ctx.letterSpacing = "0px";
  ctx.font = "400 14px 'JetBrains Mono', monospace"; ctx.fillStyle = "rgba(244,240,230,0.3)";
  ctx.fillText("Powerball draws are random · Play responsibly", 96, 574);
}

function CardCanvas({ draw, onReady }) {
  const ref = useRef(null);
  // latest-ref pattern: parents pass inline `draw` callbacks, so we render once on mount and read the newest fns
  const drawRef = useRef(draw);
  const readyRef = useRef(onReady);
  drawRef.current = draw;
  readyRef.current = onReady;
  useEffect(() => {
    let alive = true;
    (async () => {
      await loadCardFonts();
      const c = ref.current; c.width = CARD_W; c.height = CARD_H;
      drawRef.current(c.getContext("2d"));
      if (alive) readyRef.current(c.toDataURL("image/png"));
    })();
    return () => { alive = false; };
  }, []);
  return <canvas ref={ref} className="hidden" />;
}

export function ShareCardDialog({ open, onOpenChange, title, draw, text, filename, testid = "share-card" }) {
  const [dataUrl, setDataUrl] = useState("");
  const url = window.location.origin;
  useEffect(() => { if (!open) setDataUrl(""); }, [open]);

  const download = () => { const a = document.createElement("a"); a.href = dataUrl; a.download = filename; a.click(); toast.success("Card saved"); };
  const nativeShare = () => shareCardImage(dataUrl, filename, text, url);
  const copyText = () => copyToClipboard(`${text} ${url}`, "Caption copied — attach the card image when you post");
  const enc = encodeURIComponent;
  const links = [
    { name: "X", href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}` },
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}` },
    { name: "WhatsApp", href: `https://api.whatsapp.com/send?text=${enc(text + " " + url)}` },
    { name: "Telegram", href: `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}` },
    { name: "Reddit", href: `https://www.reddit.com/submit?url=${enc(url)}&title=${enc(text)}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testid}-dialog`} className="bg-surface border-white/10 text-parchment max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{title}</DialogTitle></DialogHeader>
        {open && <CardCanvas draw={draw} onReady={setDataUrl} />}
        {dataUrl ? (
          <img data-testid={`${testid}-preview`} src={dataUrl} alt="Share card" className="w-full rounded-lg border border-oracle/20" />
        ) : (
          <div className="aspect-[1200/630] w-full rounded-lg bg-black/40 label-caps flex items-center justify-center">Rendering card…</div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button data-testid={`${testid}-download-btn`} onClick={download} disabled={!dataUrl} className="bg-oracle text-ink hover:bg-[#FFC033] label-caps"><Download className="w-4 h-4 mr-2" /> Download PNG</Button>
          {navigator.share && <Button data-testid={`${testid}-native-btn`} onClick={nativeShare} disabled={!dataUrl} variant="ghost" className="text-parchment hover:text-oracle hover:bg-white/5 label-caps"><Share2 className="w-4 h-4 mr-2" /> Share…</Button>}
          <Button data-testid={`${testid}-copy-btn`} onClick={copyText} variant="ghost" className="text-parchment hover:text-oracle hover:bg-white/5 label-caps"><Copy className="w-4 h-4 mr-2" /> Copy caption</Button>
        </div>
        <div className="grid grid-cols-5 gap-2" data-testid={`${testid}-social-links`}>
          {links.map((l) => (
            <a key={l.name} href={l.href} target="_blank" rel="noreferrer" data-testid={`${testid}-${l.name.toLowerCase()}`} className="label-caps text-center text-parchment/80 hover:text-oracle border border-white/10 hover:border-oracle/40 rounded-md py-2.5 transition-colors">{l.name}</a>
          ))}
        </div>
        <p className="font-body text-[11px] text-parchment/40">Social sites don't accept images via link — download the card, then attach it with the copied caption. On mobile, "Share…" sends the image directly.</p>
      </DialogContent>
    </Dialog>
  );
}
